import { Button } from '@/shared/components/ui/Button';
import { useEscapeClose } from '@/shared/hooks/useEscapeClose';
import { ApiError } from '@/shared/lib/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Bot, Copy, Edit2, GitBranch, Loader2, Plus, Search, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  type ChatbotRow,
  activateChatbot,
  deactivateChatbot,
  deleteChatbot,
  duplicateChatbot,
  listChatbots,
} from './api';
import { ChatbotFormModal } from './ChatbotFormModal';

// Paleta determinística pra colorir o badge de canal (igual os departamentos
// têm cor própria). Mesmo nome → mesma cor.
const CHANNEL_PALETTE = [
  '#0A4EE3',
  '#10B981',
  '#F59E0B',
  '#8B5CF6',
  '#EC4899',
  '#14B8A6',
  '#0EA5E9',
  '#EF4444',
];
function channelColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return CHANNEL_PALETTE[h % CHANNEL_PALETTE.length] ?? '#0A4EE3';
}

export function ChatbotPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState<ChatbotRow | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<ChatbotRow | null>(null);
  useEscapeClose(confirmDelete !== null, () => setConfirmDelete(null));

  const listQ = useQuery({ queryKey: ['chatbots'], queryFn: listChatbots });

  const filtered = useMemo(() => {
    const all = listQ.data ?? [];
    if (!search.trim()) return all;
    const q = search.toLowerCase();
    return all.filter((b) => b.name.toLowerCase().includes(q));
  }, [listQ.data, search]);

  const toggleStatusMut = useMutation({
    mutationFn: async ({ id, active }: { id: number; active: boolean }) => {
      if (active) await activateChatbot(id);
      else await deactivateChatbot(id);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['chatbots'] }),
    onError: (err) => {
      if (err instanceof ApiError) alert(err.problem.detail ?? err.problem.title);
    },
  });

  const duplicateMut = useMutation({
    mutationFn: (id: number) => duplicateChatbot(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['chatbots'] }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteChatbot(id),
    onSuccess: () => {
      setConfirmDelete(null);
      void qc.invalidateQueries({ queryKey: ['chatbots'] });
    },
  });

  return (
    <div className="flex h-full flex-1 flex-col overflow-hidden bg-n-50">
      {/* Page header — padrão das outras páginas (igual Contatos) */}
      <header className="flex flex-shrink-0 items-center justify-between border-b border-n-200 bg-n-0 px-5 py-4">
        <div>
          <h1
            style={{
              fontSize: '22px',
              fontWeight: 700,
              letterSpacing: '-.02em',
              color: 'var(--n-900)',
            }}
          >
            Chatbots
          </h1>
          <p className="text-[12px] text-n-500">
            {`${(listQ.data?.length ?? 0).toLocaleString('pt-BR')} ${(listQ.data?.length ?? 0) === 1 ? 'chatbot' : 'chatbots'}`}
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus aria-hidden="true" />
          Novo chatbot
        </Button>
      </header>

      {/* Search bar — barra própria full-width, igual Contatos */}
      <div
        className="flex flex-shrink-0 items-center border-b border-n-200 bg-n-0 px-5 py-3"
        style={{ gap: '10px' }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'var(--bg-surface)',
            border: '1px solid var(--n-300)',
            borderRadius: 'var(--r-md)',
            height: '36px',
            padding: '0 12px',
            flex: 1,
          }}
        >
          <Search size={14} strokeWidth={1.75} style={{ color: 'var(--n-400)', flexShrink: 0 }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar chatbot…"
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontFamily: 'inherit',
              fontSize: '13px',
              color: 'var(--n-900)',
            }}
          />
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-4 overflow-auto p-[24px_20px]">
        {/* Table */}
        <div className="overflow-hidden rounded-md border border-n-200 bg-n-0 shadow-sm">
          <table className="w-full text-[12px]">
            <thead className="border-b border-n-200 bg-n-50 text-left">
              <tr>
                <th className="whitespace-nowrap px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500">
                  Nome
                </th>
                <th className="whitespace-nowrap px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500">
                  Canal vinculado
                </th>
                <th className="whitespace-nowrap px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500">
                  Status
                </th>
                <th className="whitespace-nowrap px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500">
                  Última atualização
                </th>
                <th className="whitespace-nowrap px-[14px] py-[10px] text-right text-[11px] font-bold uppercase tracking-[.05em] text-n-500">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody>
              {listQ.isLoading && (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center">
                    <Loader2 size={18} className="mx-auto animate-spin text-n-400" />
                  </td>
                </tr>
              )}
              {!listQ.isLoading && filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-12 text-center text-n-500">
                    Nenhum chatbot ainda. Clique em &quot;Novo chatbot&quot;.
                  </td>
                </tr>
              )}
              {filtered.map((b) => (
                <tr key={b.id} className="border-b border-n-100 last:border-b-0 hover:bg-n-50">
                  <td className="px-[14px] py-[11px]">
                    <div className="flex items-center gap-[10px]">
                      <div className="flex h-[32px] w-[32px] items-center justify-center rounded-md bg-brand-50 text-brand-600">
                        <Bot size={18} strokeWidth={1.75} />
                      </div>
                      <span className="font-semibold text-n-900">{b.name}</span>
                    </div>
                  </td>
                  <td className="px-[14px] py-[11px]">
                    {(() => {
                      const chans =
                        b.linked_channels && b.linked_channels.length > 0
                          ? b.linked_channels
                          : b.linked_channel
                            ? [b.linked_channel]
                            : [];
                      return chans.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {chans.map((c) => {
                            const color = channelColor(c);
                            return (
                              <span
                                key={c}
                                className="rounded-pill px-2 py-[2px] font-semibold text-[10px] uppercase tracking-[.04em]"
                                style={{ background: `${color}1f`, color }}
                              >
                                {c}
                              </span>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-[11px] text-n-400">Sem vínculo</span>
                      );
                    })()}
                  </td>
                  <td className="px-[14px] py-[11px]">
                    <button
                      type="button"
                      onClick={() =>
                        toggleStatusMut.mutate({
                          id: b.id,
                          active: b.status !== 'active',
                        })
                      }
                      disabled={toggleStatusMut.isPending}
                      className={`relative h-[20px] w-[34px] cursor-pointer rounded-pill transition-colors ${
                        b.status === 'active' ? 'bg-success-500' : 'bg-n-300'
                      }`}
                      aria-label={b.status === 'active' ? 'Desativar' : 'Ativar'}
                      title={
                        b.status === 'active'
                          ? 'Ativo — clique pra desativar'
                          : 'Inativo — reative pra começar a executar'
                      }
                    >
                      <span
                        className={`absolute top-[2px] h-[16px] w-[16px] rounded-full bg-n-0 shadow transition-all ${
                          b.status === 'active' ? 'right-[2px]' : 'left-[2px]'
                        }`}
                      />
                    </button>
                  </td>
                  <td className="px-[14px] py-[11px] text-n-600">
                    {new Date(b.updated_at).toLocaleString('pt-BR')}
                  </td>
                  <td className="px-[14px] py-[11px]">
                    <div className="flex items-center justify-end gap-1">
                      <Link
                        // biome-ignore lint/suspicious/noExplicitAny: TanStack Router types not available for dynamic params
                        to={'/chatbot/$id/editor' as any}
                        // biome-ignore lint/suspicious/noExplicitAny: same as above
                        params={{ id: String(b.id) } as any}
                        className="flex h-8 w-8 items-center justify-center rounded text-n-600 hover:bg-n-100 hover:text-brand-600"
                        title="Editar fluxo"
                      >
                        <GitBranch size={15} strokeWidth={1.75} />
                      </Link>
                      <button
                        type="button"
                        onClick={() => duplicateMut.mutate(b.id)}
                        className="flex h-8 w-8 items-center justify-center rounded text-n-600 hover:bg-n-100"
                        title="Duplicar"
                      >
                        <Copy size={15} strokeWidth={1.75} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setRenaming(b)}
                        className="flex h-8 w-8 items-center justify-center rounded text-n-600 hover:bg-n-100"
                        title="Renomear"
                      >
                        <Edit2 size={15} strokeWidth={1.75} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDelete(b)}
                        className="flex h-8 w-8 items-center justify-center rounded text-danger-500 hover:bg-danger-50"
                        title="Deletar"
                      >
                        <Trash2 size={15} strokeWidth={1.75} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-[12px] text-n-500">
          💡 <b>Dica:</b> clique em <GitBranch size={12} strokeWidth={1.75} className="inline" /> ou
          no nome pra abrir o editor visual de fluxo (drag-and-drop).
        </p>
      </div>

      {/* Modais */}
      {creating && <ChatbotFormModal onClose={() => setCreating(false)} />}
      {renaming && <ChatbotFormModal chatbot={renaming} onClose={() => setRenaming(null)} />}

      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setConfirmDelete(null);
          }}
        >
          <div className="w-full max-w-md rounded-md bg-n-0 p-6 shadow-xl">
            <h3 className="font-bold text-[16px] text-n-900">Apagar chatbot?</h3>
            <p className="mt-2 text-[13px] text-n-600">
              &quot;{confirmDelete.name}&quot; será inativado e removido. Execuções históricas serão
              preservadas.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                className="rounded border border-n-200 bg-n-0 px-3 py-2 text-[13px] font-semibold text-n-700 hover:bg-n-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => deleteMut.mutate(confirmDelete.id)}
                disabled={deleteMut.isPending}
                className="rounded bg-danger-500 px-3 py-2 text-[13px] font-semibold text-white hover:bg-danger-600 disabled:opacity-50"
              >
                {deleteMut.isPending ? 'Apagando…' : 'Apagar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
